import { useQuery } from '@apollo/client';
import { useCallback } from 'react';

import useConfig from 'app/config';
import AUDIENCE_ACCESS from 'app/hooks/use-audience-access/audience-access.gql';
import type { AudienceAccessQuery, AudienceAccessQueryVariables } from 'app/types/graphql';
import { useAppState } from 'state';

export type AudienceVerdict = 'allowed' | 'denied' | 'unavailable';

/**
 * Audience restrictions on a Resource / Resource List are honoured by hyrox-api's listing reads but
 * not by `resourceById` / `resourceListById`, so a shared link bypasses them (JS-9110). hyrox-api is
 * frozen ahead of the one-fiit-api migration, which leaves the check to the client.
 *
 * On hyrox-api an `Audience.key` IS the `UserRole` enum value (see LEGACY_AUDIENCES in
 * hyrox-api/src/graphql/types/audience/vocabulary.ts), so comparing the two here is valid — and the
 * predicate below deliberately mirrors `applyResourceUserRoleFilter` so this agrees with the listing
 * endpoints, including "a user with no role only sees unrestricted content".
 *
 * NOTE: this is a UI gate, not access control. The API still returns the full payload.
 *
 * DELETE THIS HOOK when REACT_APP_HYROX_API_URL points at one-fiit-api, and turn
 * REACT_APP_LEGACY_AUDIENCE_GATE_ENABLED off in the meantime if it misbehaves. There, an Audience is a
 * partner-scoped entity with an arbitrary key ("managers", …) and access comes from audience
 * membership rather than userRole, so this comparison would wrongly deny a permitted user. one-fiit-api
 * filters `resourceById`/`resourceListById` server-side already, so the empty-read branch in the
 * resource pages is all that's needed once we're on it.
 */
export const useAudienceAccess = () => {
  const { config } = useConfig();
  const enabled = config.REACT_APP_LEGACY_AUDIENCE_GATE_ENABLED;
  const userId = useAppState(({ auth }) => auth.userId);
  const hasUser = userId != null;

  const { data, loading, error } = useQuery<AudienceAccessQuery, AudienceAccessQueryVariables>(AUDIENCE_ACCESS, {
    variables: hasUser ? { id: userId } : undefined,
    skip: !enabled || !hasUser,
    fetchPolicy: 'cache-first',
  });

  const userRole = data?.userById?.userRole ?? null;

  /**
   * `audiences` is the restriction list: empty means "visible to every role".
   *
   * `'unavailable'` means the role read failed, so the gate cannot be evaluated — which is not the
   * same as the user being restricted. Callers should show the recoverable error page rather than
   * telling a possibly-authorised user to contact their administrator. Note an unrestricted resource
   * still resolves to `'allowed'`, since the user's role is irrelevant to it.
   */
  const checkAccess = useCallback(
    (audiences?: ReadonlyArray<{ key: string }> | null): AudienceVerdict => {
      if (!enabled || !audiences?.length) {
        return 'allowed';
      }

      if (error) {
        return 'unavailable';
      }

      return userRole && audiences.some(({ key }) => key === userRole) ? 'allowed' : 'denied';
    },
    [enabled, error, userRole],
  );

  return {
    isLoading: enabled && hasUser && loading,
    checkAccess,
  };
};

export default useAudienceAccess;
